function escapeCsvField(value: string | number | boolean | null | undefined): string {
    const s = value == null ? "" : String(value);
    if (/[",\r\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

/** UTF-8 BOM so Excel recognizes encoding when opening CSV. */
export function buildCsv(headers: string[], rows: (string | number | boolean | null | undefined)[][]): string {
    const lines = [
        headers.map(escapeCsvField).join(","),
        ...rows.map((row) => row.map(escapeCsvField).join(",")),
    ];
    return "\uFEFF" + lines.join("\r\n");
}

export function downloadTextFile(content: string, filename: string, mime = "text/csv;charset=utf-8") {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export function sanitizeReportFilename(name: string): string {
    const s = name.replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
    return s || "report";
}

export function downloadCsvReport(title: string, headers: string[], rows: (string | number | boolean | null | undefined)[][]) {
    const base = sanitizeReportFilename(title);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadTextFile(buildCsv(headers, rows), `${base}_${stamp}.csv`);
}

export async function downloadPdfTable(options: {
    title: string;
    subtitle?: string;
    head: string[];
    body: (string | number | boolean | null | undefined)[][];
    filename: string;
}) {
    const [{ jsPDF }, autoTableMod] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
    const autoTable = autoTableMod.default;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const margin = 36;
    let y = margin;
    doc.setFontSize(13);
    doc.text(options.title, margin, y);
    y += 18;
    if (options.subtitle) {
        doc.setFontSize(9);
        doc.setTextColor(90);
        doc.text(options.subtitle, margin, y);
        y += 14;
        doc.setTextColor(0);
    }
    autoTable(doc, {
        startY: y,
        head: [options.head.map((h) => String(h))],
        body: options.body.map((row) => row.map((c) => (c == null ? "" : String(c)))),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [24, 144, 255], textColor: 255 },
        margin: { left: margin, right: margin },
        tableWidth: "auto",
    });
    doc.save(options.filename);
}

export async function downloadPdfReport(title: string, subtitle: string | undefined, head: string[], body: (string | number)[][]) {
    const base = sanitizeReportFilename(title);
    const stamp = new Date().toISOString().slice(0, 10);
    await downloadPdfTable({
        title,
        subtitle,
        head,
        body,
        filename: `${base}_${stamp}.pdf`,
    });
}
